import {
  CategorySlotCount,
  FreeCashFlowAggregationPeriod,
  SlotCategory,
} from 'types/evaluation';
import { AggregationPeriod } from 'types/misc';
import { parseMomentUtc } from 'utils/dates';
import {
  SLOT_CATEGORY_COLORS,
  SLOT_CATEGORY_LABELS,
} from 'utils/slot-categories';

/**
 * Highcharts x-axis tick interval (ms) per aggregation period, for the roll-forward
 * slot-count chart. Duplicated from the eval-result aggregated-free-cash-flow utils so
 * this shared component carries no import from `pages/`.
 */
export const xAxisTickInterval: Record<
  FreeCashFlowAggregationPeriod,
  { tickInterval: number }
> = {
  year: {
    tickInterval: 365 * 24 * 3600 * 1000,
  },
  month: {
    tickInterval: 30 * 24 * 3600 * 1000,
  },
  quarter: {
    tickInterval: 3 * 30 * 24 * 3600 * 1000,
  },
};

/** Series/legend swatch color per reserve category (the shared list). */
export const categoryColors: Readonly<Record<SlotCategory, string>> =
  SLOT_CATEGORY_COLORS;

/** Display label per reserve category (the shared list). */
export const categoryLabels: Readonly<Record<SlotCategory, string>> =
  SLOT_CATEGORY_LABELS;

/**
 * Windows reserves-category slot counts to the first `limit` aggregation intervals from
 * the earliest date in the series — the single source of truth shared by the legacy
 * eval-3 aggregated-free-cash-flow view and the eval3-new Slot Count section, so both
 * render an identical window (visual parity). Note this window is a fixed interval cap,
 * NOT the relative/absolute display-settings date range (rescat never used those).
 *
 * @param {CategorySlotCount[]} slotCount - The raw slot count data.
 * @param {AggregationPeriod} aggregation - The time-interval grouping.
 * @param {number} limit - Maximum number of intervals to include (default 10).
 * @return {CategorySlotCount[]} The windowed slot counts.
 */
export const filterRescatSlotCount = (
  slotCount: CategorySlotCount[],
  aggregation: AggregationPeriod,
  limit = 10
): CategorySlotCount[] => {
  if (slotCount.length === 0) {
    return [];
  }

  const minDate = slotCount.reduce((earliest, current) => {
    return parseMomentUtc(current.date).isBefore(parseMomentUtc(earliest.date))
      ? current
      : earliest;
  }).date;

  const maxDate = parseMomentUtc(minDate)
    .startOf(aggregation)
    .add(limit, aggregation);

  return slotCount.filter((item) => {
    return parseMomentUtc(item.date).isBefore(maxDate);
  });
};
